import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, OnDestroy, OnInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { ProjectAgentService } from './project-agent.service';
import { AgentApproval, AgentMessage, AgentThreadStatus, AgentThreadSummary } from './project-agent.types';
import {
  isBusy, MAX_POLL_FAILURES, nearBottom, pickThread, pollDelayMs, pollExpired, projectChanged
} from './project-agent.logic';

const MAX_MESSAGE_CHARS = 4000;
const REMEMBER_PREFIX = 'arya_thread_';

const TOOL_LABELS: { [tool: string]: string } = {
  get_project_info: 'Reading project setup',
  get_performance: 'Checking performance',
  get_today_performance: 'Checking today',
  get_lead_status_counts: 'Counting leads by status',
  list_outcomes: 'Reading outcomes',
  get_credit_usage: 'Checking credit usage',
  get_agent_prompts: 'Reading the prompts',
  list_leads: 'Looking through leads',
  get_lead_calls: 'Reading call history',
  get_lead_timeline: 'Reading the lead timeline',
  pause_calling: 'Pause calling',
  pause_whatsapp: 'Pause WhatsApp',
  update_project_field: 'Update the project',
  update_outcome_definition: 'Update an outcome definition',
  update_agent_prompt: 'Update the agent prompt',
  add_outcome: 'Add an outcome',
  rename_outcome: 'Rename an outcome',
  remove_outcome: 'Remove an outcome',
  update_campaign_settings: 'Change campaign settings',
  update_icp: 'Update the ideal customer profile',
  add_leads: 'Add leads',
  set_tasks: 'Change follow-up tasks',
  resume_calling: 'Resume calling',
  resume_whatsapp: 'Resume WhatsApp',
  set_follow_up_limit: 'Change the follow-up limit',
  stop_campaign: 'Stop the whole campaign',
  list_eval_tests: 'Reading evaluation tests',
  list_eval_runs: 'Reading evaluation runs',
  get_eval_run: 'Reading an evaluation result',
  generate_eval_tests: 'Create evaluation tests',
  start_eval_run: 'Run evaluations',
  cancel_eval_run: 'Cancel an evaluation run'
};

const FIELD_LABELS: { [field: string]: string } = {
  projectName: 'Project name',
  projectDescription: 'Description',
  callAgentName: 'Calling agent name',
  whatsappAgentName: 'WhatsApp agent name',
  initialGreeting: 'Opening greeting',
  whatsappChatPrompt: 'WhatsApp agent prompt',
  whatsappDefaultMessage: 'Default WhatsApp message',
  whatsappInitialMessage: 'First WhatsApp message',
  userPrompt: 'Agent prompt'
};

export const SUGGESTIONS = [
  'How did we do today?',
  'Which outcome is dropping this week?',
  'Show me the funnel for the last 7 days',
  'Help me improve the agent prompt'
];

@Component({
  selector: 'hour4u-admin-project-agent',
  templateUrl: './project-agent.component.html',
  styleUrls: ['./project-agent.component.scss']
})
export class ProjectAgentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectDetails: any = null;
  /** Panel visibility. The component stays mounted when closed so a running turn keeps polling. */
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  /** Fired after Arya finishes a change the owner approved, so the page can reload the project. */
  @Output() projectEdited = new EventEmitter<void>();
  @ViewChild('scroller', { static: false }) scroller: ElementRef;
  @ViewChild('composer', { static: false }) composer: ElementRef;

  profile = this.appService.getCurrentAccount();
  suggestions = SUGGESTIONS;
  maxChars = MAX_MESSAGE_CHARS;

  threads: AgentThreadSummary[] = [];
  threadId = '';
  status: AgentThreadStatus = 'idle';
  messages: AgentMessage[] = [];
  approvals: AgentApproval[] = [];
  draft = '';
  sending = false;
  deciding: { [approvalId: string]: boolean } = {};
  loadError = '';
  historyOpen = false;
  /** We stopped watching a long turn: show "Check again" instead of an endless spinner. */
  stalled = false;
  /** Several polls in a row failed: the server may still be working, we just cannot see it. */
  connectionLost = false;

  private destroyed$ = new Subject<void>();
  private poll: Subscription;
  private pollTimer: any = null;
  private pollFailures = 0;
  /** False once the reader scrolls up, so new data never drags them back to the bottom. */
  private stickToBottom = true;
  private changeInFlight = false;

  constructor(
    private agent: ProjectAgentService,
    private appService: AppService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadThreads(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projectDetails && !changes.projectDetails.firstChange) {
      // The page reloads the same project after edits; that must not wipe the chat.
      if (projectChanged(changes.projectDetails.previousValue, changes.projectDetails.currentValue)) {
        this.newChat();
        this.loadThreads(true);
      }
    }
    if (changes.open && this.open) {
      this.scrollSoon(true);
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get ready(): boolean {
    return !!(this.projectDetails && this.projectDetails.project_id && this.profile && this.profile.entity);
  }

  get busy(): boolean {
    return isBusy(this.sending, this.status, this.stalled, this.connectionLost);
  }

  get unwatched(): boolean {
    return this.status === 'running' && (this.stalled || this.connectionLost);
  }

  get waitingForApproval(): boolean {
    return this.status === 'waiting_approval' && this.approvals.length > 0;
  }

  get canSend(): boolean {
    const text = this.draft.trim();
    return this.ready && !this.busy && !this.waitingForApproval && text.length > 0 && text.length <= MAX_MESSAGE_CHARS;
  }

  private get scope() {
    return { entityId: this.profile.entity.id, projectId: this.projectDetails.project_id };
  }

  private get user() {
    const meta = (this.profile.user && this.profile.user.userMeta) || {};
    return { userId: this.profile.user.id, userName: meta.name || '' };
  }

  toolLabel(tool: string): string {
    return TOOL_LABELS[tool] || tool;
  }

  /** What the agent wants to change, in the owner's words. */
  describeApproval(approval: AgentApproval): { title: string; detail: string; value: string } {
    const args = approval.args || {};
    if (approval.tool === 'update_project_field') {
      return {
        title: `Update ${FIELD_LABELS[args.field] || args.field}`,
        detail: args.reason || '',
        value: args.value || ''
      };
    }
    if (approval.tool === 'update_outcome_definition') {
      return {
        title: `Update the "${args.outcome_name || ''}" outcome definition`,
        detail: args.reason || '',
        value: args.new_definition || ''
      };
    }
    if (approval.tool === 'update_agent_prompt') {
      return { title: 'Replace the agent prompt', detail: args.reason || '', value: args.new_prompt || '' };
    }
    if (approval.tool === 'add_outcome') {
      return {
        title: `Add the outcome "${args.outcome_name || ''}"`,
        detail: args.reason || '',
        value: args.outcome_definition || ''
      };
    }
    if (approval.tool === 'rename_outcome') {
      return {
        title: `Rename "${args.outcome_name || ''}" to "${args.new_name || ''}"`,
        detail: args.reason || '',
        value: 'Earlier calls keep the old label.'
      };
    }
    if (approval.tool === 'remove_outcome') {
      return { title: `Remove the outcome "${args.outcome_name || ''}"`, detail: args.reason || '', value: '' };
    }
    if (approval.tool === 'set_follow_up_limit') {
      return {
        title: `Allow up to ${args.max_touchpoints} touchpoints per lead`,
        detail: args.reason || '',
        value: 'Calls and WhatsApp messages both count.'
      };
    }
    if (approval.tool === 'stop_campaign') {
      return {
        title: 'Stop the whole campaign',
        detail: args.reason || '',
        value: 'Pauses calling and WhatsApp and switches off follow-ups. Leads and history are kept.'
      };
    }
    if (approval.tool === 'start_eval_run' || approval.tool === 'generate_eval_tests') {
      return { title: this.toolLabel(approval.tool), detail: args.reason || '', value: 'This uses credits.' };
    }
    if (approval.tool === 'add_leads') {
      const leads = Array.isArray(args.leads) ? args.leads : [];
      const lines = leads.map(l => `${l.name || ''}  +${l.country_code || ''} ${l.mobile || ''}`);
      return { title: `Add ${leads.length} lead${leads.length === 1 ? '' : 's'}`, detail: args.reason || '', value: lines.join('\n') };
    }
    // Everything else: show exactly what will be sent, so nothing is hidden.
    const { reason, ...rest } = args as any;
    const shown = Object.keys(rest).length ? JSON.stringify(rest, null, 2) : '';
    return { title: this.toolLabel(approval.tool), detail: reason || '', value: shown };
  }

  loadThreads(openLatest = false) {
    if (!this.ready) {
      return;
    }
    this.agent.listThreads(this.scope).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        this.threads = (res && res.threads) || [];
        if (openLatest && !this.threadId && this.threads.length) {
          this.openThread(pickThread(this.threads, this.remembered()));
        }
      },
      () => {
        this.loadError = 'Could not load earlier chats. Close and reopen Arya to try again.';
      }
    );
  }

  close() {
    this.historyOpen = false;
    this.closed.emit();
  }

  toggleHistory() {
    this.historyOpen = !this.historyOpen;
  }

  newChat() {
    this.historyOpen = false;
    this.stopPolling();
    this.threadId = '';
    this.messages = [];
    this.approvals = [];
    this.status = 'idle';
    this.draft = '';
    this.loadError = '';
    this.stalled = false;
    this.connectionLost = false;
    this.remember('');
    this.resizeSoon();
  }

  openThread(threadId: string) {
    this.historyOpen = false;
    this.stopPolling();
    this.threadId = threadId;
    this.loadError = '';
    this.stalled = false;
    this.connectionLost = false;
    this.remember(threadId);
    this.refresh(true);
  }

  useSuggestion(text: string) {
    this.draft = text;
    this.send();
  }

  send() {
    if (!this.canSend) {
      return;
    }
    const text = this.draft.trim();
    this.sending = true;
    const optimistic: AgentMessage = { role: 'user', content: text };
    this.messages = [...this.messages, optimistic];
    this.draft = '';
    this.resizeSoon();
    this.scrollSoon(true);

    this.agent.send(this.scope, this.user, text, this.threadId).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        this.sending = false;
        this.threadId = res.threadId;
        this.remember(res.threadId);
        this.status = 'running';
        this.startPolling();
      },
      err => {
        this.sending = false;
        this.messages = this.messages.filter(m => m !== optimistic);
        this.draft = text;
        this.resizeSoon();
        this.notify.showMessage(this.messageFrom(err, 'Could not send. Please try again.'), 5000);
      }
    );
  }

  decide(approval: AgentApproval, decision: 'approve' | 'reject') {
    if (this.deciding[approval.approval_id]) {
      return;
    }
    this.deciding[approval.approval_id] = true;
    this.agent.decide(this.scope, this.user, this.threadId, approval.approval_id, decision)
      .pipe(takeUntil(this.destroyed$)).subscribe(
        () => {
          this.deciding[approval.approval_id] = false;
          this.approvals = this.approvals.filter(a => a.approval_id !== approval.approval_id);
          this.status = 'running';
          this.changeInFlight = decision === 'approve';
          this.startPolling();
        },
        err => {
          this.deciding[approval.approval_id] = false;
          this.notify.showMessage(this.messageFrom(err, 'Could not record your decision.'), 5000);
          this.refresh();
        }
      );
  }

  /** Grow the message box with its content (up to the CSS max-height, then scroll). */
  private resizeSoon() {
    setTimeout(() => this.autosize());
  }

  autosize() {
    const el: HTMLTextAreaElement = this.composer && this.composer.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 2 + 'px';
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** The owner's own "Check again": look at the server once more. */
  recheck() {
    this.stalled = false;
    this.connectionLost = false;
    this.loadError = '';
    this.refresh(true);
  }

  private refresh(scrollToEnd = false) {
    if (!this.threadId || !this.ready) {
      return;
    }
    this.agent.getThread(this.scope, this.threadId).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        this.applyThread(res.thread ? res.thread.status : undefined, res.messages, res.pending_approvals);
        if (this.status === 'running') {
          this.startPolling();
        }
        if (scrollToEnd) {
          this.scrollSoon(true);
        }
      },
      () => {
        this.loadError = 'Could not load this chat. Use "Check again" in a moment.';
        this.connectionLost = this.status === 'running';
      }
    );
  }

  private applyThread(status: AgentThreadStatus | undefined, messages: AgentMessage[], approvals: AgentApproval[]) {
    const before = this.messages.length;
    this.status = status || 'idle';
    this.messages = messages || [];
    this.approvals = approvals || [];
    this.cdr.markForCheck();
    // Follow new content only when something arrived; an unchanged poll must not move the view.
    if (this.messages.length !== before || this.approvals.length) {
      this.scrollSoon();
    }
  }

  /** Watch a running turn. Eases off after a minute, survives a few failed polls, and
   *  after 15 minutes hands control back instead of spinning forever. */
  private startPolling() {
    this.stopPolling();
    this.stalled = false;
    this.connectionLost = false;
    this.pollFailures = 0;
    const startedAt = Date.now();
    const schedule = () => {
      this.pollTimer = setTimeout(tick, pollDelayMs(Date.now() - startedAt));
    };
    const tick = () => {
      this.poll = this.agent.getThread(this.scope, this.threadId).pipe(takeUntil(this.destroyed$)).subscribe(
        res => {
          this.pollFailures = 0;
          this.loadError = '';
          this.applyThread(res.thread ? res.thread.status : undefined, res.messages, res.pending_approvals);
          if (this.status !== 'running') {
            this.stopPolling();
            this.loadThreads();
            if (this.changeInFlight) {
              this.changeInFlight = false;
              this.projectEdited.emit();
            }
          } else if (pollExpired(Date.now() - startedAt)) {
            this.stopPolling();
            this.stalled = true;
          } else {
            schedule();
          }
        },
        () => {
          this.pollFailures++;
          if (this.pollFailures >= MAX_POLL_FAILURES) {
            this.stopPolling();
            this.connectionLost = true;
            this.loadError = 'Lost connection to Arya. She may still be working; use "Check again".';
          } else {
            schedule();
          }
        }
      );
    };
    tick();
  }

  private stopPolling() {
    if (this.poll) {
      this.poll.unsubscribe();
      this.poll = null;
    }
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  onScroll() {
    const el = this.scroller && this.scroller.nativeElement;
    if (el) {
      this.stickToBottom = nearBottom(el);
    }
  }

  private scrollSoon(force = false) {
    if (force) {
      this.stickToBottom = true;
    }
    setTimeout(() => {
      const el = this.scroller && this.scroller.nativeElement;
      if (el && this.stickToBottom) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }

  private remembered(): string {
    try {
      return sessionStorage.getItem(REMEMBER_PREFIX + this.projectDetails.project_id) || '';
    } catch (e) {
      return '';
    }
  }

  private remember(threadId: string) {
    try {
      const key = REMEMBER_PREFIX + this.projectDetails.project_id;
      if (threadId) {
        sessionStorage.setItem(key, threadId);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      // Private mode or storage blocked: the chat still works, it just is not remembered.
    }
  }

  private messageFrom(err: any, fallback: string): string {
    const body = err && err.error;
    if (body && typeof body.error === 'string') {
      return body.error;
    }
    return fallback;
  }
}
